/**
 * The tone a score's LABEL is drawn in, chosen by the payload's own machine
 * code rather than by matching its prose.
 *
 * ── THIS MODULE USED TO BE THE ONE EXEMPTION, AND IS NOT ANY MORE ───────────
 *
 * `clientScience.test.ts` forbids any string the backend's fixtures carry from
 * appearing as a literal in the client, because a client that writes a governed
 * string has become the author of one. Until schema 1.4 this module had to
 * write all three of `ALLOWED_SCORE_LABELS` down in order to map them to a
 * colour, and it held the single scoped exemption in that scan (owner decision
 * O3) with a four-way control around it.
 *
 * Backend ask B2 shipped in `eeg-tracking 0.9.0`: `label_codes[slot]` carries
 * `around | mild | strong` beside every `labels[slot]`. The keys below are
 * those tokens. **No governed string is written here any more, and the
 * exemption has been deleted rather than left dormant** — an exemption that
 * outlives its reason is how the next one gets granted.
 *
 * ── THERE IS STILL NO DIRECTION TO DRAW ─────────────────────────────────────
 *
 * The codes come from `abs(z)` (`result_writer.label_index_for_z`). The sign is
 * gone before the band is chosen, so "above" and "below" do not exist and
 * green/amber cannot be built from what arrives. A shift from baseline is
 * neither good nor bad, so the three tones are neutral, cyan and purple, and an
 * unrecognised code gets cyan — the design's own fallback — rather than being
 * dropped.
 *
 * NO ARITHMETIC AND NO COMPARISON live here, so this file needs no
 * `NOT_SCIENCE` entry. Nothing here returns text to render either: the caller
 * renders the payload's own words and passes the tone to a class name. That is
 * what `clientScience.test.ts`'s replacement control asserts.
 */
export type LabelTone = 'neutral' | 'cyan' | 'purple'

/**
 * A `Map`, not an object literal. `TONES['constructor']` on an object resolves
 * to `Object.prototype.constructor` and would make an unknown code from the
 * wire behave like a hit; `Map.get` answers `undefined` for every key but the
 * three that were put in it.
 */
const TONES: ReadonlyMap<string, LabelTone> = new Map<string, LabelTone>([
  ['around', 'neutral'],
  ['mild', 'cyan'],
  ['strong', 'purple'],
])

/**
 * A slot with no reading has no mark to tone, so it stays grey.
 *
 * `null` also covers a recording scored before schema 1.4, whose payload
 * carries no `label_codes` at all. Neutral is the honest answer there: the
 * alternative is inferring the band from the words, which is the string match
 * this field exists to remove.
 */
export function labelTone(labelCode: string | null): LabelTone {
  if (labelCode === null) return 'neutral'
  return TONES.get(labelCode) ?? 'cyan'
}
